import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { INK, MUTED, RULE } from '../../analytics/analyticsUi';

// Selection bar pinned to the bottom of the screen, with confirmation dialogs
const BulkActions = ({
  selectedCount,
  selectedItems = [],
  onClearSelection,
  onBulkRefresh,
  onBulkDelete
}) => {
  const [dialog, setDialog] = useState(null); // 'refresh' | 'delete' | null
  const [working, setWorking] = useState(false);

  if (!selectedCount) return null;

  const plural = selectedCount === 1 ? 'account' : 'accounts';

  const confirm = async () => {
    setWorking(true);
    try {
      const ids = selectedItems.map((item) => item._id);
      if (dialog === 'refresh') await onBulkRefresh(ids);
      if (dialog === 'delete') {
        await onBulkDelete(ids);
        onClearSelection();
      }
      setDialog(null);
    } catch (err) {
      // The page shows the error message
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Paper
        role="region"
        aria-label="Selected accounts"
        elevation={0}
        sx={{
          position: 'fixed',
          bottom: { xs: 12, sm: 24 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          width: { xs: 'calc(100% - 24px)', sm: 'auto' },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          pl: 2.5,
          pr: 1,
          py: 1,
          bgcolor: INK,
          color: '#fff',
          borderRadius: 2,
          boxShadow: '0 12px 32px rgba(31, 42, 61, 0.28)'
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: { sm: 1 } }}>
          {selectedCount} {plural} selected
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={() => setDialog('refresh')} sx={{ color: '#fff', textTransform: 'none' }}>
          Refresh
        </Button>
        <Button size="small" onClick={() => setDialog('delete')} sx={{ color: '#FFB199', textTransform: 'none' }}>
          Delete
        </Button>
        <IconButton size="small" onClick={onClearSelection} aria-label="Clear selection" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>

      <Dialog open={!!dialog} onClose={() => !working && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
          {dialog === 'delete' ? `Delete ${selectedCount} ${plural}?` : `Refresh ${selectedCount} ${plural}?`}
        </DialogTitle>
        <DialogContent>
          {dialog === 'delete' ? (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              The accounts are removed from the portal. This can't be undone.
            </Alert>
          ) : (
            <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
              Details for each account are reloaded from Meta. This can take a moment for several accounts.
            </Typography>
          )}
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, maxHeight: 240, overflow: 'auto' }}>
            {selectedItems.map((item) => (
              <Box component="li" key={item._id} sx={{ py: 1, borderTop: `1px solid ${RULE}` }}>
                <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{item.businessName}</Typography>
                <Typography variant="caption" sx={{ color: MUTED }}>{item.phoneNumber}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(null)} disabled={working} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={confirm}
            variant="contained"
            color={dialog === 'delete' ? 'error' : 'primary'}
            disabled={working}
            startIcon={working ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            {dialog === 'delete'
              ? (working ? 'Deleting…' : `Delete ${plural}`)
              : (working ? 'Refreshing…' : `Refresh ${plural}`)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkActions;
